import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { 
  CheckCircle2, 
  Settings, 
  Bell, 
  GitPullRequest, 
  Zap, 
  Search, 
  Filter,
  Cloud,
  MessageSquare,
  Shield,
  Wrench,
  ArrowRight,
  X
} from 'lucide-react';

/* ===================== Styled Components ===================== */
const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  min-height: 100vh;
  background: #fafbfc;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PageSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 350px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 0.875rem;
  border: 1px solid ${({ active }) => active ? '#3b82f6' : '#e2e8f0'};
  background: ${({ active }) => active ? '#3b82f6' : 'white'};
  color: ${({ active }) => active ? 'white' : '#64748b'};
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: #e2e8f0;
  }
`;

/* ==== FIXED to prevent overlap ==== */
const CardHeader = styled.div`
  padding: 3rem 1.5rem 1rem; /* increased top padding */
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const CardStatus = styled.div`
  position: absolute;
  top: 1rem;
  left: 1.5rem; /* moved inward */
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: ${({ connected }) =>
    connected
      ? 'rgba(34, 197, 94, 0.1)'
      : 'rgba(156, 163, 175, 0.1)'};
  color: ${({ connected }) => (connected ? '#059669' : '#6b7280')};
  border: 1px solid
    ${({ connected }) =>
      connected ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)'};
`;

const CategoryBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1.5rem; /* moved inward */
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: ${({ category }) => {
    const colors = {
      devops: '#10b981',
      communication: '#8b5cf6',
      security: '#ef4444',
      cloud: '#06b6d4',
    };
    return colors[category] || '#6b7280';
  }};
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CardLogo = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f5f9;
  
  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const CardBody = styled.div`
  padding: 0.75rem 1.5rem 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const CardDescription = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const CardFeatures = styled.ul`
  margin: 1rem 0;
  padding: 0;
  list-style: none;
`;

const CardFeature = styled.li`
  font-size: 0.8125rem;
  color: #475569;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    flex-shrink: 0;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
    padding: 0.125rem;
    border-radius: 3px;
  }
`;

const CardFooter = styled.div`
  padding: 0 1.5rem 1.5rem;
`;

const ConnectButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;

  background: ${({ connected }) =>
    connected
      ? '#f1f5f9'
      : '#3b82f6'};
  color: ${({ connected }) => connected ? '#475569' : 'white'};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ connected }) =>
      connected
        ? '0 4px 12px rgba(0, 0, 0, 0.1)'
        : '0 4px 12px rgba(59, 130, 246, 0.3)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #64748b;
  
  h3 {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    color: #475569;
  }
  
  p {
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }
`;

const ClearFiltersButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

/* ===================== Integration Card Component ===================== */
function IntegrationCard({ 
  title, 
  description, 
  logoUrl, 
  connected, 
  onConnect,
  features,
  category
}) {
  return (
    <Card>
      <CardHeader>
        <CardStatus connected={connected}>
          {connected ? (
            <>
              <CheckCircle2 size={12} />
              Connected
            </>
          ) : 'Available'}
        </CardStatus>
        <CategoryBadge category={category}>
          {category}
        </CategoryBadge>
        <CardLogo>
          <img src={logoUrl} alt={title} />
        </CardLogo>
      </CardHeader>
      <CardBody>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        
        <CardFeatures>
          {features.map((feature, index) => (
            <CardFeature key={index}>
              {feature.icon}
              {feature.text}
            </CardFeature>
          ))}
        </CardFeatures>
      </CardBody>
      <CardFooter>
        <ConnectButton 
          connected={connected} 
          onClick={onConnect}
        >
          {connected ? (
            <>
              <Settings size={14} />
              Manage
              <ArrowRight size={12} />
            </>
          ) : (
            <>
              <Zap size={14} />
              Connect
              <ArrowRight size={12} />
            </>
          )}
        </ConnectButton>
      </CardFooter>
    </Card>
  );
}

/* ===================== Main Integration Page Component ===================== */
const IntegrationPage = () => {
  const [integrations, setIntegrations] = useState({
    jira: false,
    slack: true,
    github: false,
    jenkins: false,
    aws: true,
    azure: false,
    sonarqube: false,
    docker: false,
    teams: false,
    confluence: false,
    pagerduty: false,
    splunk: false,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const handleConnect = (integration) => {
    setIntegrations((prev) => ({
      ...prev,
      [integration]: !prev[integration],
    }));
  };

  const integrationData = [
    {
      id: 'jira',
      title: 'Jira',
      description: "Sync vulnerabilities as Jira issues and track resolution progress with automated workflows",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg",
      category: 'devops',
      features: [
        { text: "Auto-create security tickets", icon: <Bell size={14} /> },
        { text: "Status synchronization", icon: <Settings size={14} /> },
        { text: "Custom workflow mapping", icon: <GitPullRequest size={14} /> }
      ]
    },
    {
      id: 'slack',
      title: 'Slack',
      description: "Real-time security notifications and team collaboration in your workspace channels",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/slack/slack-original.svg",
      category: 'communication',
      features: [
        { text: "Instant vulnerability alerts", icon: <Bell size={14} /> },
        { text: "Team mention notifications", icon: <MessageSquare size={14} /> },
        { text: "Custom alert channels", icon: <Settings size={14} /> }
      ]
    },
    {
      id: 'github',
      title: 'GitHub',
      description: "Map vulnerabilities to code and monitor security fixes with automated PR scanning",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
      category: 'devops',
      features: [
        { text: "PR security scanning", icon: <GitPullRequest size={14} /> },
        { text: "Code reference linking", icon: <Settings size={14} /> },
        { text: "Auto-comment on commits", icon: <Bell size={14} /> }
      ]
    },
    {
      id: 'jenkins',
      title: 'Jenkins',
      description: "Automated security testing in your CI/CD pipeline with build failure notifications",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg",
      category: 'devops',
      features: [
        { text: "Pipeline security gates", icon: <Shield size={14} /> },
        { text: "Build failure alerts", icon: <Bell size={14} /> },
        { text: "Scan result reporting", icon: <GitPullRequest size={14} /> }
      ]
    },
    {
      id: 'aws',
      title: 'Amazon Web Services',
      description: "Monitor AWS resources for security vulnerabilities and compliance violations",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
      category: 'cloud',
      features: [
        { text: "Resource security scanning", icon: <Cloud size={14} /> },
        { text: "Compliance monitoring", icon: <Shield size={14} /> },
        { text: "Cost optimization alerts", icon: <Bell size={14} /> }
      ]
    },
    {
      id: 'azure',
      title: 'Microsoft Azure',
      description: "Integrate with Azure Security Center for comprehensive cloud security monitoring",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
      category: 'cloud',
      features: [
        { text: "Security Center integration", icon: <Cloud size={14} /> },
        { text: "Resource vulnerability alerts", icon: <Shield size={14} /> },
        { text: "Compliance reporting", icon: <Settings size={14} /> }
      ]
    },
    {
      id: 'sonarqube',
      title: 'SonarQube',
      description: "Code quality and security analysis with detailed vulnerability reporting",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sonarqube/sonarqube-original.svg",
      category: 'security',
      features: [
        { text: "Static code analysis", icon: <Shield size={14} /> },
        { text: "Security hotspot detection", icon: <Bell size={14} /> },
        { text: "Quality gate integration", icon: <Settings size={14} /> }
      ]
    },
    {
      id: 'docker',
      title: 'Docker',
      description: "Container security scanning and vulnerability assessment for Docker images",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
      category: 'devops',
      features: [
        { text: "Image vulnerability scanning", icon: <Shield size={14} /> },
        { text: "Registry integration", icon: <Settings size={14} /> },
        { text: "Build-time security checks", icon: <Wrench size={14} /> }
      ]
    },
    {
      id: 'teams',
      title: 'Microsoft Teams',
      description: "Security notifications and team collaboration in Microsoft Teams channels",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg",
      category: 'communication',
      features: [
        { text: "Channel notifications", icon: <MessageSquare size={14} /> },
        { text: "Alert customization", icon: <Bell size={14} /> },
        { text: "Team mention support", icon: <Settings size={14} /> }
      ]
    },
    {
      id: 'confluence',
      title: 'Confluence',
      description: "Document security processes and maintain vulnerability knowledge base",
      logoUrl: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/confluence/confluence-original.svg",
      category: 'devops',
      features: [
        { text: "Security documentation", icon: <Settings size={14} /> },
        { text: "Knowledge base updates", icon: <GitPullRequest size={14} /> },
        { text: "Process automation", icon: <Wrench size={14} /> }
      ]
    },
    {
      id: 'pagerduty',
      title: 'PagerDuty',
      description: "Critical security incident management and on-call alerting system",
      logoUrl: "https://cdn.brandfolder.io/5H442O3W/at/pl546j-7le8zk-4nzzs8/PagerDuty_icon.svg",
      category: 'communication',
      features: [
        { text: "Critical incident alerts", icon: <Bell size={14} /> },
        { text: "On-call escalation", icon: <MessageSquare size={14} /> },
        { text: "Incident tracking", icon: <Settings size={14} /> }
      ]
    },
    {
      id: 'splunk',
      title: 'Splunk',
      description: "Security information and event management with advanced analytics",
      logoUrl: "https://cdn.worldvectorlogo.com/logos/splunk.svg",
      category: 'security',
      features: [
        { text: "SIEM integration", icon: <Shield size={14} /> },
        { text: "Security analytics", icon: <Settings size={14} /> },
        { text: "Event correlation", icon: <Wrench size={14} /> }
      ]
    }
  ];

  const filteredIntegrations = useMemo(() => {
    return integrationData.filter((integration) => {
      const matchesSearch = integration.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          integration.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = activeFilter === 'all' || integration.category === activeFilter;
      return matchesSearch && matchesFilter;
    });
  }, [searchTerm, activeFilter]);

  const activeCount = Object.values(integrations).filter(Boolean).length;
  const totalCount = Object.keys(integrations).length;

  const categories = [
    { id: 'all', label: 'All', icon: <Filter size={14} /> },
    { id: 'devops', label: 'DevOps', icon: <Wrench size={14} /> },
    { id: 'communication', label: 'Communication', icon: <MessageSquare size={14} /> },
    { id: 'security', label: 'Security', icon: <Shield size={14} /> },
    { id: 'cloud', label: 'Cloud', icon: <Cloud size={14} /> }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderTop>
          <div>
            <PageTitle>Integrations</PageTitle>
            <PageSubtitle>
              Connect your tools to streamline security workflows and enhance collaboration
            </PageSubtitle>
          </div>
          <StatsContainer>
            <StatItem>
              <StatNumber>{activeCount}</StatNumber>
              <StatLabel>Active</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{totalCount}</StatNumber>
              <StatLabel>Available</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{Math.round((activeCount / totalCount) * 100)}%</StatNumber>
              <StatLabel>Coverage</StatLabel>
            </StatItem>
          </StatsContainer>
        </HeaderTop>
      </PageHeader>

      <ControlsContainer>
        <SearchContainer>
          <SearchIcon size={16} />
          <SearchInput
            type="text"
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterContainer>
          {categories.map((category) => (
            <FilterButton
              key={category.id}
              active={activeFilter === category.id}
              onClick={() => setActiveFilter(category.id)}
            >
              {category.icon}
              {category.label}
            </FilterButton>
          ))}
        </FilterContainer>
      </ControlsContainer>
      
      {filteredIntegrations.length > 0 ? (
        <CardsContainer>
          {filteredIntegrations.map((integration) => (
            <IntegrationCard
              key={integration.id}
              title={integration.title}
              description={integration.description}
              logoUrl={integration.logoUrl}
              connected={integrations[integration.id]}
              onConnect={() => handleConnect(integration.id)}
              features={integration.features}
              category={integration.category}
            />
          ))}
        </CardsContainer>
      ) : (
        <EmptyState>
          <h3>No integrations found</h3>
          <p>Try adjusting your search terms or filters</p>
          <ClearFiltersButton onClick={() => {
            setSearchTerm('');
            setActiveFilter('all');
          }}>
            Clear all filters
          </ClearFiltersButton>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default IntegrationPage;
